import dayjs from 'dayjs'

/**
 * Event length is authored as a duration but STORED as an end instant
 * (`ends_at`), because that is what the API takes. These helpers convert
 * between the two so the form can offer "1 hour" while the payload keeps
 * sending a timestamp.
 */

/** Sentinel for "the user picks the end date/time by hand". */
export const EVENT_LENGTH_CUSTOM = 'custom'

/**
 * Selected when nothing else applies — a new event, or an existing one with no
 * `ends_at`. Every event now carries a length, so there is no "no end time"
 * choice; opening and saving a legacy end-less event gives it a 1-hour end.
 */
export const EVENT_LENGTH_DEFAULT = '60'

export interface EventLengthOption {
  label: string
  /** Minutes, or the `EVENT_LENGTH_CUSTOM` sentinel. */
  value: string
}

/**
 * Dropdown choices, in order. Values are stringified minutes so the select
 * works in strings throughout (antd convention) — `buildEndsAt` parses them.
 */
export const EVENT_LENGTH_OPTIONS: EventLengthOption[] = [
  { label: '15 Minutes', value: '15' },
  { label: '30 Minutes', value: '30' },
  { label: '1 Hour', value: '60' },
  { label: '2 Hours', value: '120' },
  { label: '4 Hours', value: '240' },
  { label: 'Custom', value: EVENT_LENGTH_CUSTOM },
]

/**
 * The preset minute values, for deriving a selection from an existing event.
 *
 * Derived from the options above rather than hand-listed — the two MUST agree,
 * or an event whose duration matches a preset would still resolve to Custom.
 */
const PRESET_MINUTES = EVENT_LENGTH_OPTIONS.map((option) =>
  Number(option.value)
).filter((minutes) => Number.isFinite(minutes))

/**
 * Derive which dropdown entry an existing event matches.
 *
 * Falls back to Custom rather than snapping to the nearest preset: a 45-minute
 * event must keep its 45 minutes, and silently rounding it on edit would change
 * data the user never touched.
 */
export const deriveEventLength = (
  startsAt: string | null | undefined,
  endsAt: string | null | undefined
): string => {
  // A legacy event with no end has no length to derive — fall back to the
  // default rather than offering a choice that no longer exists.
  if (!endsAt) {
    return EVENT_LENGTH_DEFAULT
  }

  const start = dayjs(startsAt)
  const end = dayjs(endsAt)
  if (!startsAt || !start.isValid() || !end.isValid()) {
    return EVENT_LENGTH_CUSTOM
  }

  const minutes = end.diff(start, 'minute')
  return PRESET_MINUTES.includes(minutes)
    ? String(minutes)
    : EVENT_LENGTH_CUSTOM
}

/**
 * Which dropdown entry a stated duration matches.
 *
 * A repeating event carries `series.duration_minutes` outright, which is more
 * authoritative than diffing one occurrence's start/end. Same no-rounding rule
 * as `deriveEventLength`: an off-preset duration resolves to Custom rather than
 * snapping. Falls back to `fallback` when no duration is given.
 */
export const deriveEventLengthFromMinutes = (
  minutes: number | null | undefined,
  fallback: string
): string => {
  if (
    typeof minutes !== 'number' ||
    !Number.isFinite(minutes) ||
    minutes <= 0
  ) {
    return fallback
  }
  return PRESET_MINUTES.includes(minutes)
    ? String(minutes)
    : EVENT_LENGTH_CUSTOM
}

/**
 * The `ends_at` a given length implies.
 *
 * Returns:
 * - `null` for Custom, meaning "leave whatever the user typed alone"
 * - `''` when the start is unusable, so the caller writes an empty (controlled)
 *   value rather than an Invalid Date
 * - otherwise a UTC ISO string `length` minutes after `startsAt`
 */
export const buildEndsAt = (
  startsAt: string | null | undefined,
  length: string
): string | null => {
  if (length === EVENT_LENGTH_CUSTOM) {
    return null
  }

  const minutes = Number(length)
  const start = dayjs(startsAt)
  if (!startsAt || !start.isValid() || !Number.isFinite(minutes)) {
    return ''
  }

  return start.add(minutes, 'minute').toISOString()
}
